import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const initialForm = {
  contactName: '',
  email: '',
  phone: '',
  organizationName: '',
  role: '',
  organizationType: '',
  organizationSize: '',
  website: '',
  currentTools: '',
  painPoints: '',
  goals: '',
  timeline: '',
};

const orgTypes = [
  'Youth sports club',
  'League',
  'Tournament operator',
  'Training facility',
  'School or academy',
  'Other',
];

const orgSizes = [
  '1-3 teams',
  '4-10 teams',
  '11-25 teams',
  '26-50 teams',
  '50+ teams',
];

const timelines = [
  'As soon as possible',
  'This season',
  'Next season',
  'Just researching',
];

const Field = ({ label, name, value, onChange, required = false, type = 'text', placeholder, autoComplete }) => (
  <label className="block">
    <span className="text-sm font-semibold text-gray-800">
      {label}{required && <span className="text-emerald-700"> *</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-3 text-gray-950 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
    />
  </label>
);

const SelectField = ({ label, name, value, onChange, options, required = false }) => (
  <label className="block">
    <span className="text-sm font-semibold text-gray-800">
      {label}{required && <span className="text-emerald-700"> *</span>}
    </span>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-3 text-gray-950 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
    >
      <option value="">Select one</option>
      {options.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>
  </label>
);

const TextAreaField = ({ label, name, value, onChange, required = false, placeholder, rows = 4 }) => (
  <label className="block">
    <span className="text-sm font-semibold text-gray-800">
      {label}{required && <span className="text-emerald-700"> *</span>}
    </span>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      rows={rows}
      placeholder={placeholder}
      className="mt-2 w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-3 text-gray-950 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
    />
  </label>
);

const RequestDemo = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/public/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to submit your request.');
      }

      setSubmitted(true);
      setForm(initialForm);
    } catch (submitError) {
      setError(submitError.message || 'Unable to submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-950">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3" aria-label="FieldHouse home">
            <img src="/fieldhouse-logo.png" alt="FieldHouse" className="h-10 w-auto" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:text-gray-950">
            <ArrowLeftIcon className="h-4 w-4" />
            Back to homepage
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8 lg:py-16">
        <section>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Request a demo</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-normal text-gray-950 sm:text-5xl">
            Show us how your organization runs today.
          </h1>
          <p className="mt-5 text-lg leading-8 text-gray-700">
            Share the basics about your club, league, or facility and the workflows you want to improve. The FieldHouse team will follow up with a focused walkthrough.
          </p>
          <div className="mt-8 space-y-4">
            {[
              'Organization size and structure',
              'Current tools, spreadsheets, and manual processes',
              'Registration, payment, communication, and evaluation pain points',
              'Timeline for rolling out a new operating system',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 text-sm font-medium text-gray-700">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-emerald-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {submitted ? (
            <div className="py-10 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-gray-950">Demo request sent</h2>
              <p className="mx-auto mt-3 max-w-md text-gray-700">
                Thanks. We sent your request to the FieldHouse team at admin@fieldhouse.club.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50"
                >
                  Send another request
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                >
                  Return home
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-none" />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Contact name"
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  placeholder="Your name"
                />
                <Field
                  label="Work email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  type="email"
                  autoComplete="email"
                  placeholder="name@organization.com"
                />
                <Field
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  autoComplete="tel"
                  placeholder="Optional"
                />
                <Field
                  label="Role / title"
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  placeholder="Director, coach, admin"
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Organization name"
                  name="organizationName"
                  value={form.organizationName}
                  onChange={handleChange}
                  required
                  autoComplete="organization"
                  placeholder="Club or league name"
                />
                <Field
                  label="Website"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  type="url"
                  placeholder="https://"
                />
                <SelectField
                  label="Organization type"
                  name="organizationType"
                  value={form.organizationType}
                  onChange={handleChange}
                  options={orgTypes}
                />
                <SelectField
                  label="Organization size"
                  name="organizationSize"
                  value={form.organizationSize}
                  onChange={handleChange}
                  options={orgSizes}
                  required
                />
              </div>

              <TextAreaField
                label="What tools or processes are you using today?"
                name="currentTools"
                value={form.currentTools}
                onChange={handleChange}
                placeholder="Examples: spreadsheets, TeamSnap, SportsEngine, email lists, payment links"
                rows={3}
              />

              <TextAreaField
                label="Current pain points"
                name="painPoints"
                value={form.painPoints}
                onChange={handleChange}
                required
                placeholder="What is slowing down your admins, coaches, parents, or registration process?"
              />

              <TextAreaField
                label="What would you like FieldHouse to help with?"
                name="goals"
                value={form.goals}
                onChange={handleChange}
                placeholder="Share the workflows or outcomes you care about most."
              />

              <SelectField
                label="Timeline"
                name="timeline"
                value={form.timeline}
                onChange={handleChange}
                options={timelines}
              />

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? 'Sending request...' : 'Request a demo'}
                {!submitting && <ArrowRightIcon className="h-5 w-5" />}
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
};

export default RequestDemo;
